import { Color, Geometry, Mesh, Program, RenderTarget, Renderer, Vec2 } from "ogl"
import { useEffect, useRef } from "react"

const DISPLAY_FRAGMENT = /* glsl */ `
  precision highp float;

  uniform sampler2D tFluid;
  uniform float uTime;
  varying vec2 vUv;
  varying vec2 vL;
  varying vec2 vR;
  varying vec2 vT;
  varying vec2 vB;

  void main() {
    vec3 fluid = texture2D(tFluid, vUv).rgb;
    vec3 fluidL = texture2D(tFluid, vL).rgb;
    vec3 fluidR = texture2D(tFluid, vR).rgb;
    vec3 fluidT = texture2D(tFluid, vT).rgb;
    vec3 fluidB = texture2D(tFluid, vB).rgb;

    float centerEnergy = clamp(length(fluid), 0.0, 1.0);
    float centerLuma = clamp(dot(fluid, vec3(0.299, 0.587, 0.114)), 0.0, 1.0);

    float neighborEnergy = (
      length(fluidL) +
      length(fluidR) +
      length(fluidT) +
      length(fluidB)
    ) * 0.25;

    float neighborLuma = (
      dot(fluidL, vec3(0.299, 0.587, 0.114)) +
      dot(fluidR, vec3(0.299, 0.587, 0.114)) +
      dot(fluidT, vec3(0.299, 0.587, 0.114)) +
      dot(fluidB, vec3(0.299, 0.587, 0.114))
    ) * 0.25;

    float base = smoothstep(0.015, 0.62, centerEnergy) * 0.19;
    float lumaBand = clamp((centerLuma - neighborLuma) * 0.34 + 0.075, -0.04, 0.07);
    float ridge = clamp((centerEnergy - neighborEnergy) * 0.55, -0.08, 0.09);
    float tone = clamp(base + lumaBand + ridge, 0.0, 0.28);
    float alpha = clamp(base * 0.95 + max(ridge, 0.0) * 0.55, 0.0, 0.26);
    vec3 color = vec3(tone);

    gl_FragColor = vec4(color, alpha);
  }
`

const BASE_VERTEX = /* glsl */ `
  precision highp float;
  attribute vec2 position;
  attribute vec2 uv;
  varying vec2 vUv;
  varying vec2 vL;
  varying vec2 vR;
  varying vec2 vT;
  varying vec2 vB;
  uniform vec2 texelSize;

  void main () {
    vUv = uv;
    vL = vUv - vec2(texelSize.x, 0.0);
    vR = vUv + vec2(texelSize.x, 0.0);
    vT = vUv + vec2(0.0, texelSize.y);
    vB = vUv - vec2(0.0, texelSize.y);
    gl_Position = vec4(position, 0.0, 1.0);
  }
`

const CLEAR_SHADER = /* glsl */ `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  uniform sampler2D uTexture;
  uniform float value;

  void main () {
    gl_FragColor = value * texture2D(uTexture, vUv);
  }
`

const SPLAT_SHADER = /* glsl */ `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uTarget;
  uniform float aspectRatio;
  uniform vec3 color;
  uniform vec2 point;
  uniform float radius;

  void main () {
    vec2 p = vUv - point.xy;
    p.x *= aspectRatio;
    vec3 splat = exp(-dot(p, p) / radius) * color;
    vec3 base = texture2D(uTarget, vUv).xyz;
    gl_FragColor = vec4(base + splat, 1.0);
  }
`

const ADVECTION_MANUAL_FILTERING_SHADER = /* glsl */ `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform sampler2D uSource;
  uniform vec2 texelSize;
  uniform vec2 dyeTexelSize;
  uniform float dt;
  uniform float dissipation;

  vec4 bilerp(sampler2D sam, vec2 uv, vec2 tsize) {
    vec2 st = uv / tsize - 0.5;
    vec2 iuv = floor(st);
    vec2 fuv = fract(st);
    vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
    vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
    vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
    vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);
    return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
  }

  void main () {
    vec2 coord = vUv - dt * bilerp(uVelocity, vUv, texelSize).xy * texelSize;
    gl_FragColor = dissipation * bilerp(uSource, coord, dyeTexelSize);
    gl_FragColor.a = 1.0;
  }
`

const ADVECTION_SHADER = /* glsl */ `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  uniform sampler2D uVelocity;
  uniform sampler2D uSource;
  uniform vec2 texelSize;
  uniform float dt;
  uniform float dissipation;

  void main () {
    vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
    gl_FragColor = dissipation * texture2D(uSource, coord);
    gl_FragColor.a = 1.0;
  }
`

const DIVERGENCE_SHADER = /* glsl */ `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uVelocity, vL).x;
    float R = texture2D(uVelocity, vR).x;
    float T = texture2D(uVelocity, vT).y;
    float B = texture2D(uVelocity, vB).y;
    vec2 C = texture2D(uVelocity, vUv).xy;

    if (vL.x < 0.0) L = -C.x;
    if (vR.x > 1.0) R = -C.x;
    if (vT.y > 1.0) T = -C.y;
    if (vB.y < 0.0) B = -C.y;

    float div = 0.5 * (R - L + T - B);
    gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
  }
`

const CURL_SHADER = /* glsl */ `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uVelocity, vL).y;
    float R = texture2D(uVelocity, vR).y;
    float T = texture2D(uVelocity, vT).x;
    float B = texture2D(uVelocity, vB).x;
    float vorticity = R - L - T + B;
    gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
  }
`

const VORTICITY_SHADER = /* glsl */ `
  precision highp float;
  precision highp sampler2D;
  varying vec2 vUv;
  varying vec2 vL;
  varying vec2 vR;
  varying vec2 vT;
  varying vec2 vB;
  uniform sampler2D uVelocity;
  uniform sampler2D uCurl;
  uniform float curl;
  uniform float dt;

  void main () {
    float L = texture2D(uCurl, vL).x;
    float R = texture2D(uCurl, vR).x;
    float T = texture2D(uCurl, vT).x;
    float B = texture2D(uCurl, vB).x;
    float C = texture2D(uCurl, vUv).x;
    vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
    force /= length(force) + 0.0001;
    force *= curl * C;
    force.y *= -1.0;
    vec2 vel = texture2D(uVelocity, vUv).xy;
    gl_FragColor = vec4(vel + force * dt, 0.0, 1.0);
  }
`

const PRESSURE_SHADER = /* glsl */ `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uPressure;
  uniform sampler2D uDivergence;

  void main () {
    float L = texture2D(uPressure, vL).x;
    float R = texture2D(uPressure, vR).x;
    float T = texture2D(uPressure, vT).x;
    float B = texture2D(uPressure, vB).x;
    float divergence = texture2D(uDivergence, vUv).x;
    float pressure = (L + R + B + T - divergence) * 0.25;
    gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
  }
`

const GRADIENT_SUBTRACT_SHADER = /* glsl */ `
  precision mediump float;
  precision mediump sampler2D;
  varying highp vec2 vUv;
  varying highp vec2 vL;
  varying highp vec2 vR;
  varying highp vec2 vT;
  varying highp vec2 vB;
  uniform sampler2D uPressure;
  uniform sampler2D uVelocity;

  void main () {
    float L = texture2D(uPressure, vL).x;
    float R = texture2D(uPressure, vR).x;
    float T = texture2D(uPressure, vT).x;
    float B = texture2D(uPressure, vB).x;
    vec2 velocity = texture2D(uVelocity, vUv).xy;
    velocity.xy -= vec2(R - L, T - B);
    gl_FragColor = vec4(velocity, 0.0, 1.0);
  }
`

type OglRenderTargetOptions = ConstructorParameters<typeof RenderTarget>[1]

type PointerSplat = {
  dx: number
  dy: number
  x: number
  y: number
}

const SIM_RESOLUTION = 128
const DYE_RESOLUTION = 512
const PRESSURE_ITERATIONS = 3
const DENSITY_DISSIPATION = 0.97
const VELOCITY_DISSIPATION = 0.98
const PRESSURE_DISSIPATION = 0.8
const CURL_STRENGTH = 20
const SPLAT_RADIUS = 0.2

export function OglFluidBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const renderer = new Renderer({
      alpha: true,
      antialias: false,
      canvas,
      dpr: Math.min(window.devicePixelRatio || 1, 2),
      powerPreference: "high-performance",
    })
    const gl = renderer.gl
    const gl2 = gl as WebGL2RenderingContext
    gl.clearColor(1, 1, 1, 0)

    const resize = () => {
      renderer.dpr = Math.min(window.devicePixelRatio || 1, 2)
      renderer.setSize(window.innerWidth, window.innerHeight)
    }

    const getSupportedFormat = (
      internalFormat: number,
      format: number,
      type: number,
    ): { format: number; internalFormat: number } | null => {
      if (!supportsRenderTextureFormat(internalFormat, format, type)) {
        if (internalFormat === gl2.R16F) return getSupportedFormat(gl2.RG16F, gl2.RG, type)
        if (internalFormat === gl2.RG16F) return getSupportedFormat(gl2.RGBA16F, gl.RGBA, type)
        return null
      }

      return { format, internalFormat }
    }

    const supportsRenderTextureFormat = (internalFormat: number, format: number, type: number) => {
      const texture = gl.createTexture()
      if (!texture) return false
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null)

      const framebuffer = gl.createFramebuffer()
      if (!framebuffer) return false
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)

      return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE
    }

    const createDoubleFBO = (options: OglRenderTargetOptions = {}) => {
      const fbo = {
        read: new RenderTarget(gl, options),
        swap: () => {
          const temp = fbo.read
          fbo.read = fbo.write
          fbo.write = temp
        },
        write: new RenderTarget(gl, options),
      }
      return fbo
    }

    const texelSize = { value: new Vec2(1 / SIM_RESOLUTION) }
    const linearExtension = gl.renderer.extensions[`OES_texture_${gl.renderer.isWebgl2 ? "" : "half_"}float_linear`]
    const halfFloat = gl.renderer.isWebgl2
      ? gl2.HALF_FLOAT
      : gl.renderer.extensions.OES_texture_half_float.HALF_FLOAT_OES

    const filtering = linearExtension ? gl.LINEAR : gl.NEAREST

    let rgba: { format: number; internalFormat: number } | null
    let rg: { format: number; internalFormat: number } | null
    let red: { format: number; internalFormat: number } | null

    if (gl.renderer.isWebgl2) {
      rgba = getSupportedFormat(gl2.RGBA16F, gl.RGBA, halfFloat)
      rg = getSupportedFormat(gl2.RG16F, gl2.RG, halfFloat)
      red = getSupportedFormat(gl2.R16F, gl2.RED, halfFloat)
    } else {
      rgba = getSupportedFormat(gl.RGBA, gl.RGBA, halfFloat)
      rg = rgba
      red = rgba
    }

    if (!rgba || !rg || !red) {
      return
    }

    const density = createDoubleFBO({
      depth: false,
      format: rgba.format,
      height: DYE_RESOLUTION,
      internalFormat: rgba.internalFormat,
      minFilter: filtering,
      type: halfFloat,
      width: DYE_RESOLUTION,
    })

    const velocity = createDoubleFBO({
      depth: false,
      format: rg.format,
      height: SIM_RESOLUTION,
      internalFormat: rg.internalFormat,
      minFilter: filtering,
      type: halfFloat,
      width: SIM_RESOLUTION,
    })

    const pressure = createDoubleFBO({
      depth: false,
      format: red.format,
      height: SIM_RESOLUTION,
      internalFormat: red.internalFormat,
      minFilter: gl.NEAREST,
      type: halfFloat,
      width: SIM_RESOLUTION,
    })

    const divergence = new RenderTarget(gl, {
      depth: false,
      format: red.format,
      height: SIM_RESOLUTION,
      internalFormat: red.internalFormat,
      minFilter: gl.NEAREST,
      type: halfFloat,
      width: SIM_RESOLUTION,
    })

    const curl = new RenderTarget(gl, {
      depth: false,
      format: red.format,
      height: SIM_RESOLUTION,
      internalFormat: red.internalFormat,
      minFilter: gl.NEAREST,
      type: halfFloat,
      width: SIM_RESOLUTION,
    })

    const triangle = new Geometry(gl, {
      position: { data: new Float32Array([-1, -1, 3, -1, -1, 3]), size: 2 },
      uv: { data: new Float32Array([0, 0, 2, 0, 0, 2]), size: 2 },
    })

    const clearProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: CLEAR_SHADER,
        uniforms: {
          texelSize,
          uTexture: { value: null },
          value: { value: PRESSURE_DISSIPATION },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const splatProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: SPLAT_SHADER,
        uniforms: {
          aspectRatio: { value: 1 },
          color: { value: new Color() },
          point: { value: new Vec2() },
          radius: { value: SPLAT_RADIUS / 100 },
          texelSize,
          uTarget: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const advectionProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: linearExtension ? ADVECTION_SHADER : ADVECTION_MANUAL_FILTERING_SHADER,
        uniforms: {
          dissipation: { value: 1 },
          dt: { value: 0.016 },
          dyeTexelSize: { value: new Vec2(1 / DYE_RESOLUTION) },
          texelSize,
          uSource: { value: null },
          uVelocity: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const divergenceProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: DIVERGENCE_SHADER,
        uniforms: {
          texelSize,
          uVelocity: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const curlProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: CURL_SHADER,
        uniforms: {
          texelSize,
          uVelocity: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const vorticityProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: VORTICITY_SHADER,
        uniforms: {
          curl: { value: CURL_STRENGTH },
          dt: { value: 0.016 },
          texelSize,
          uCurl: { value: null },
          uVelocity: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const pressureProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: PRESSURE_SHADER,
        uniforms: {
          texelSize,
          uDivergence: { value: null },
          uPressure: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const gradientSubtractProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: GRADIENT_SUBTRACT_SHADER,
        uniforms: {
          texelSize,
          uPressure: { value: null },
          uVelocity: { value: null },
        },
        vertex: BASE_VERTEX,
      }),
    })

    const splats: PointerSplat[] = []
    const lastMouse = new Vec2()

    const updatePointer = (event: MouseEvent | TouchEvent) => {
      let x: number | undefined
      let y: number | undefined

      if ("changedTouches" in event && event.changedTouches.length > 0) {
        x = event.changedTouches[0].clientX
        y = event.changedTouches[0].clientY
      } else if ("clientX" in event) {
        x = event.clientX
        y = event.clientY
      }

      if (x == null || y == null) return

      if (!(lastMouse as Vec2 & { isInit?: boolean }).isInit) {
        ;(lastMouse as Vec2 & { isInit?: boolean }).isInit = true
        lastMouse.set(x, y)
      }

      const deltaX = x - lastMouse.x
      const deltaY = y - lastMouse.y
      lastMouse.set(x, y)

      if (Math.abs(deltaX) || Math.abs(deltaY)) {
        splats.push({
          dx: deltaX * 5,
          dy: deltaY * -5,
          x: x / gl.renderer.width,
          y: 1 - y / gl.renderer.height,
        })
      }
    }

    const splat = ({ x, y, dx, dy }: PointerSplat) => {
      splatProgram.program.uniforms.uTarget.value = velocity.read.texture
      splatProgram.program.uniforms.aspectRatio.value = gl.renderer.width / gl.renderer.height
      splatProgram.program.uniforms.point.value.set(x, y)
      splatProgram.program.uniforms.color.value.set(dx, dy, 1)

      gl.renderer.render({ scene: splatProgram, sort: false, target: velocity.write, update: false })
      velocity.swap()

      splatProgram.program.uniforms.uTarget.value = density.read.texture
      gl.renderer.render({ scene: splatProgram, sort: false, target: density.write, update: false })
      density.swap()
    }

    const displayProgram = new Mesh(gl, {
      geometry: triangle,
      program: new Program(gl, {
        depthTest: false,
        depthWrite: false,
        fragment: DISPLAY_FRAGMENT,
        uniforms: {
          tFluid: { value: null },
          uTime: { value: 0 },
        },
        vertex: BASE_VERTEX,
      }),
    })

    resize()

    const isTouchCapable = "ontouchstart" in window
    window.addEventListener("resize", resize)
    if (isTouchCapable) {
      window.addEventListener("touchstart", updatePointer, { passive: true })
      window.addEventListener("touchmove", updatePointer, { passive: true })
    } else {
      window.addEventListener("mousemove", updatePointer)
    }

    let frameId = 0
    const update = (time: number) => {
      frameId = window.requestAnimationFrame(update)

      gl.renderer.autoClear = false

      for (let index = splats.length - 1; index >= 0; index -= 1) {
        const next = splats.splice(index, 1)[0]
        if (next) splat(next)
      }

      curlProgram.program.uniforms.uVelocity.value = velocity.read.texture
      gl.renderer.render({ scene: curlProgram, sort: false, target: curl, update: false })

      vorticityProgram.program.uniforms.uVelocity.value = velocity.read.texture
      vorticityProgram.program.uniforms.uCurl.value = curl.texture
      gl.renderer.render({ scene: vorticityProgram, sort: false, target: velocity.write, update: false })
      velocity.swap()

      divergenceProgram.program.uniforms.uVelocity.value = velocity.read.texture
      gl.renderer.render({ scene: divergenceProgram, sort: false, target: divergence, update: false })

      clearProgram.program.uniforms.uTexture.value = pressure.read.texture
      gl.renderer.render({ scene: clearProgram, sort: false, target: pressure.write, update: false })
      pressure.swap()

      pressureProgram.program.uniforms.uDivergence.value = divergence.texture
      for (let index = 0; index < PRESSURE_ITERATIONS; index += 1) {
        pressureProgram.program.uniforms.uPressure.value = pressure.read.texture
        gl.renderer.render({ scene: pressureProgram, sort: false, target: pressure.write, update: false })
        pressure.swap()
      }

      gradientSubtractProgram.program.uniforms.uPressure.value = pressure.read.texture
      gradientSubtractProgram.program.uniforms.uVelocity.value = velocity.read.texture
      gl.renderer.render({ scene: gradientSubtractProgram, sort: false, target: velocity.write, update: false })
      velocity.swap()

      advectionProgram.program.uniforms.dyeTexelSize.value.set(1 / SIM_RESOLUTION)
      advectionProgram.program.uniforms.uVelocity.value = velocity.read.texture
      advectionProgram.program.uniforms.uSource.value = velocity.read.texture
      advectionProgram.program.uniforms.dissipation.value = VELOCITY_DISSIPATION
      gl.renderer.render({ scene: advectionProgram, sort: false, target: velocity.write, update: false })
      velocity.swap()

      advectionProgram.program.uniforms.dyeTexelSize.value.set(1 / DYE_RESOLUTION)
      advectionProgram.program.uniforms.uVelocity.value = velocity.read.texture
      advectionProgram.program.uniforms.uSource.value = density.read.texture
      advectionProgram.program.uniforms.dissipation.value = DENSITY_DISSIPATION
      gl.renderer.render({ scene: advectionProgram, sort: false, target: density.write, update: false })
      density.swap()

      gl.renderer.autoClear = true

      displayProgram.program.uniforms.tFluid.value = density.read.texture
      displayProgram.program.uniforms.uTime.value = time * 0.001

      gl.renderer.render({ scene: displayProgram, sort: false, update: false })
    }

    frameId = window.requestAnimationFrame(update)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener("resize", resize)
      if (isTouchCapable) {
        window.removeEventListener("touchstart", updatePointer)
        window.removeEventListener("touchmove", updatePointer)
      } else {
        window.removeEventListener("mousemove", updatePointer)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      data-background
      className="pointer-events-none fixed inset-0 z-0 h-full w-full"
    />
  )
}
